import { getSnapshot, types, destroy } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import dayjs from 'dayjs';

import { cmhc_rate as cmhcRate } from '@ratehub/mortgage-calculations';

import getMongoSafeExperiments from '../functions/getMongoSafeExperiments';
import getTransactionTypeFromPurpose from '../functions/getTransactionTypeFromPurpose';
import getIsOwnerOccupiedFromIntendedUse from '../functions/getIsOwnerOccupiedFromIntendedUse';
import PURPOSES, { PurposeEnum } from '../definitions/Purpose';
import { partiallyUpdateModel, removeEmptyProperties } from '../definitions/Common';
import remapJourneyToTransactionType from '../functions/remapJourneyToTransactionType';
import Contact from './Contact';
import Calculators from './Calculators';
import Inquiry from './Inquiry';
import Mortgage from './Mortgage';
import Application from './Application';
import Property from './Property';


const Journey = types
    .model('Journey', {
        id: types.optional(types.identifier, uuid),
        purpose: PurposeEnum,
        agreedPrivacyPolicy: types.optional(types.boolean, false),
        contacts: types.array(Contact),
        inquiries: types.array(Inquiry),
        mortgage: types.optional(Mortgage, {}),
        subjectProperty: types.optional(Property, {}),
        application: types.optional(Application, {}),
        calculators: types.maybe(Calculators),
    })
    .views(self => ({
        contactAtRank(applicationRank) {
            return self.contacts.find(({ applicationRank: contactRank }) => contactRank === applicationRank);
        },
        relationshipToPrimaryContact(contact) {
            return self.application.relationships
                .find(({ contact: relationshipContact }) => relationshipContact === contact);
        },
        get primaryContact() {
            return self.contactAtRank(1);
        },
        get secondaryContacts() {
            return self.contacts.filter(contact => contact !== self.primaryContact);
        },
        get validationState() {
            return self.application.metadata.validation;
        },
        get allProperties() {
            return [ self.subjectProperty, ...self.application.otherProperties ];
        },
        get transactionType() {
            return getTransactionTypeFromPurpose(self.purpose);
        },
        getProperty({ id }) {
            if (self.subjectProperty.id === id) {
                return self.subjectProperty;
            }

            return self.allProperties.find(property => property.id === id);
        },
    }))
    .actions(self => ({
        updateLastEditDate() {
            const now = dayjs();

            self.application.metadata.lastEditDateClient = now.toISOString();
            self.application.metadata.lastEditDate = now.toISOString();
        },
        /**
         *
         * @param {Journey} changes
         */
        update(values) {
            if (values.inquiries) {
                throw new Error('You cannot mutate inquiries, they are read-only snapshots.');
            }

            if (values.application?.metadata) {
                throw new Error('You cannot make updates to metadata directly.');
            }

            // Remember the old transaction type in case they are updating it
            const transactionType = self.transactionType;

            partiallyUpdateModel(self, values);

            // Important: Update related transaction type fields AFTER the model has been updated so we don't miss those fields.
            if (typeof values.purpose !== 'undefined') {
                self.updateTransactionTypeFields(transactionType, self.transactionType);
            }

            // Renewal date of existing mortgage is coincidentally the same as the closing date of prospective mortgage
            if (self.purpose === PURPOSES.RENEWAL) {
                if (values.application?.closingDate && self.subjectProperty?.existingMortgage) {
                    self.subjectProperty.existingMortgage.renewalDate = values.application.closingDate;
                } else if (values.subjectProperty?.existingMortgage?.renewalDate && self.application) {
                    self.application.closingDate = values.subjectProperty.existingMortgage.renewalDate;
                }
            }

            // TODO: Remove this when the property metadata is calculated
            if (
                // dependencies to avoid extra updates on every update
                typeof values.purpose !== 'undefined'
                || typeof values.subjectProperty?.intendedUse !== 'undefined'
                || typeof values.subjectProperty?.existingMortgage?.currentMortgageBalance !== 'undefined'
            ) {
                self.updatePropertyMetadata();
            }

            if (
                typeof values.purpose !== 'undefined'
                || typeof values.mortgage !== 'undefined'
                || typeof values.subjectProperty?.existingMortgage?.currentMortgageBalance !== 'undefined'
            ) {
                self.applyMortgageCalculations();
            }

            self.updateLastEditDate();
        },
        updateTransactionTypeFields(oldTransactionType, newTransactionType) {
            if (oldTransactionType === newTransactionType) {
                return; // nothing to do
            }

            remapJourneyToTransactionType(self, oldTransactionType, newTransactionType);
        },
        setContact(applicationRank, snapshot) {
            const existingContact = self.contactAtRank(applicationRank);

            if (snapshot) {
                existingContact
                    ? partiallyUpdateModel(existingContact, snapshot)
                    : self.contacts.push({ applicationRank, ...removeEmptyProperties(snapshot) });
            } else if (existingContact) {
                self.removeContact(applicationRank);
            }

            self.updateLastEditDate();
        },
        removeContactByRank(applicationRank) {
            const existingContact = self.contactAtRank(applicationRank);

            if (existingContact) {
                self.setContactRelationshipToPrimary(applicationRank); // delete relationship

                // remove metadata associated with contact
                self.allProperties.forEach(property => {
                    const metadata = property.contactPropertyMetadata.find(({ contact }) => contact.id === existingContact.id);

                    if (metadata) {
                        destroy(metadata);
                    }

                    // delete other properties that are no longer associated with any contacts
                    if (self.subjectProperty !== property && property.contactPropertyMetadata.length === 0) {
                        destroy(property);
                    }
                });

                destroy(existingContact);
            }

            self.updateLastEditDate();
        },
        setContactRelationshipToPrimary(applicationRank, relationshipToPrimary) {
            const contact = self.contactAtRank(applicationRank);
            if (applicationRank > 1) {
                const existingRelationship = self.application.relationships
                    .find(({ contact: relationshipContact }) => relationshipContact === contact);
                if (existingRelationship) {
                    if (relationshipToPrimary) {
                        existingRelationship.relationshipType = relationshipToPrimary;
                    } else {
                        destroy(existingRelationship);
                    }
                } else if (relationshipToPrimary) {
                    self.application.relationships.push({
                        relationshipType: relationshipToPrimary,
                        contact: contact.id,
                        relationTo: self.primaryContact.id,
                    });
                }
            }
        },
        setContactEmploymentHistory(applicationRank, employmentHistory) {
            const existingHistory = self.contactAtRank(applicationRank).employment;

            if (existingHistory) {
                const foundEmployments = [];

                employmentHistory.forEach((employment, i) => {
                    const existingEmployment = existingHistory.find(e => e.id === employment.id);
                    if (existingEmployment) {
                        partiallyUpdateModel(existingEmployment, employment);
                        foundEmployments.push(existingEmployment);
                    } else {
                        existingHistory.splice(i, 0, removeEmptyProperties(employment));
                        foundEmployments.push(existingHistory[i]);
                    }
                });

                existingHistory.filter(e => !foundEmployments.includes(e)).forEach(e => destroy(e));
            } else {
                self.contactAtRank(applicationRank).employment = employmentHistory
                    .map(employment => {
                        removeEmptyProperties(employment);
                    });
            }
        },
        setProperty({ id, ...snapshot }) {
            const existingProperty = self.getProperty({ id });

            if (existingProperty) {
                partiallyUpdateModel(existingProperty, snapshot);
            } else {
                self.application.otherProperties.push(removeEmptyProperties({ id, ...snapshot }));
            }

            self.updateLastEditDate();

            return self.getProperty({ id });
        },
        removeProperty({ id }) {
            const property = self.getProperty({ id });

            if (!property) {
                // eslint-disable-next-line no-console
                console.warn(`Cannot delete property: No property found with id: "${id}"`);

                return;
            }

            if (self.subjectProperty === property) {
                throw new Error('Cannot delete property: Attempted to remove the subject property.');
            }

            destroy(property);
            self.updateLastEditDate();
        },
        inquire({ type, rate, agent, provider, brokerage, contact, mortgage, subjectProperty, experiments = [], request, submissionSource, ...inquiry }) {
            if (!type) {
                throw new Error('Must specify an inquiry type');
            }

            if (contact) {
                self.setContact(1, contact);
            }

            if (mortgage) {
                self.update({ mortgage });
            }

            if (subjectProperty) {
                self.update({ subjectProperty });
            }

            self.applyMortgageCalculations(); // make sure the mortgage calculations are up to date

            self.inquiries.push({
                ...inquiry,
                date: new Date(),
                type,
                ...(rate && { rate: {
                    id: rate.id,
                    value: rate.value,
                    regularPayment: rate.regularPayment,
                    lumpSum: rate.lumpSum,
                    description: rate.description,
                    rateHold: rate.rateHold,
                    insurable: rate.insuranceBucket,
                    isPreApproval: rate.isPreApproval ?? null,
                } }),
                ...(agent && { agent: {
                    id: agent.id,
                    email: agent.email,
                } }),
                provider: {
                    id: provider.id,
                    slug: provider.slug,
                    name: provider.name,
                },
                brokerage: brokerage && {
                    id: brokerage.id,
                    slug: brokerage.slug,
                    name: brokerage.name,
                } || undefined,
                mortgage: {
                    ...removeEmptyProperties(getSnapshot(self.mortgage)),
                    ...(self.subjectProperty.existingMortgage && removeEmptyProperties(getSnapshot(self.subjectProperty.existingMortgage))),
                },
                submissionSource: submissionSource ?? self.application.metadata.documentSource,
                experiments: Object.fromEntries(getMongoSafeExperiments(experiments)), // capture all the experiments
                request,
            });

            self.updateLastEditDate();
        },
        setValidationState(page, state) {
            self.validationState.set(page, state);
        },
        removeValidationState(page) {
            self.validationState.delete(page);
        },
        setLastVisited(lastVisited) {
            self.application.metadata.lastVisitedSection = lastVisited;
        },
        // TODO: This can be removed when we are on v3
        setIsConverted(isConverted) {
            self.application.metadata.isConverted = isConverted;
        },
        // TODO: Remove this when the resulting state is calculated.
        updatePropertyMetadata() {
            if (self.transactionType !== PURPOSES.PURCHASE) {
                if (getIsOwnerOccupiedFromIntendedUse(self.subjectProperty.intendedUse)) {
                    self.subjectProperty.setContactOwnership({
                        contact: self.primaryContact,
                        livesAt: true,
                        currentAddress: true,
                        hasMortgage: self.subjectProperty.existingMortgage?.currentMortgageBalance > 0,
                    });
                } else {
                    self.subjectProperty.removeContactResidence(self.primaryContact);
                }
            }
        },
        applyMortgageCalculations() {
            // will turn these into computeds when the backend is better setup for it
            const { mortgage } = self;

            switch (self.transactionType) {
                case PURPOSES.PURCHASE: {
                    if (mortgage.purchasePrice && mortgage.downPayment && mortgage.amortization) {
                        const cmhcInsurance = cmhcRate(1 - mortgage.downPayment / mortgage.purchasePrice)
                            * (mortgage.purchasePrice - mortgage.downPayment);
                        const mortgageAmount = mortgage.purchasePrice - mortgage.downPayment + cmhcInsurance;

                        partiallyUpdateModel(self, {
                            mortgage: {
                                insurancePremium: cmhcInsurance,
                                mortgageAmount: mortgageAmount,
                            },
                        });
                    }
                    break;
                }
                case PURPOSES.RENEWAL:
                case PURPOSES.REFINANCE: {
                    const currentMortgageBalance = self.subjectProperty?.existingMortgage?.currentMortgageBalance;
                    const propertyValue = self.subjectProperty?.homeValue;

                    let additionalFundsRequired = mortgage.additionalFundsRequired;

                    // Update renewal to refinance if they request additional funds
                    if (self.purpose === PURPOSES.RENEWAL && typeof additionalFundsRequired === 'number' && additionalFundsRequired > 0) {
                        self.purpose = PURPOSES.REFINANCE;
                    }

                    if (
                        self.transactionType === PURPOSES.REFINANCE
                        && currentMortgageBalance != null
                        && propertyValue !== null
                    ) {
                        const availableEquity = Math.max(propertyValue * 0.8 - currentMortgageBalance, 0);

                        additionalFundsRequired = Math.min(mortgage.additionalFundsRequired ?? 0, availableEquity); // Make sure additional funds do not exceed available equity
                    }

                    if (currentMortgageBalance >= 0) {
                        partiallyUpdateModel(self, {
                            mortgage: {
                                currentMortgageBalance,
                                mortgageAmount: currentMortgageBalance + (mortgage.additionalFundsRequired ?? 0),
                                additionalFundsRequired,
                            },
                        });
                    }
                    break;
                }
                default: break;
            }
        },
    }));

export default Journey;
